import type {FetchBaseQueryError, FetchBaseQueryMeta} from "@reduxjs/toolkit/query";
import {ColorKey} from "../../../utilis/types/Types";
import {unwrapData} from "../../apiResponse.ts";
import {api} from "../../api.ts";
export type CallState = keyof typeof CallStateEnum
export type CallKind = 'EXTERNAL' | 'INTERNAL' | 'CONFERENCE' | 'QUEUED';
export type GroupCallOutcome = 'OUTGOING' | 'OUTGOING_UNANSWERED' | 'JOINED' | 'DECLINED' | 'MISSED';
export interface CallParticipant {
    userId: number;
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
    joinedAt?: string | null;
    declinedAt?: string | null;
}
export interface CallUser {
    userId: number;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
}
export type RecordingStatus = 'AVAILABLE' | 'DELETED';
export interface CallRecordingInfo {
    id: number;
    status: RecordingStatus;
    deletedAt?: string | null;
}
export type CallReason =
    | 'NEW_INQUIRY'
    | 'PRICING_QUOTE'
    | 'ORDER_BOOKING'
    | 'APPOINTMENT'
    | 'SUPPORT_ISSUE'
    | 'BILLING_PAYMENT'
    | 'COMPLAINT'
    | 'CANCELLATION'
    | 'ACCOUNT_CHANGE'
    | 'DELIVERY_STATUS'
    | 'FOLLOW_UP'
    | 'PARTNER_VENDOR'
    | 'WRONG_NUMBER_SPAM'
    | 'OTHER';
export type CallOutcome =
    | 'RESOLVED'
    | 'INFO_PROVIDED'
    | 'FOLLOW_UP_NEEDED'
    | 'CALLBACK_REQUESTED'
    | 'MEETING_SCHEDULED'
    | 'ORDER_PLACED'
    | 'QUOTE_REQUESTED'
    | 'ESCALATED'
    | 'UNRESOLVED'
    | 'VOICEMAIL'
    | 'NO_CONVERSATION'
    | 'OTHER';
export type SentimentLabel = 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE' | 'MIXED';
export type NextStepOwner = 'AGENT' | 'CLIENT' | 'BOTH' | 'UNKNOWN';
export type FollowUpChannel = 'SMS' | 'EMAIL';
export interface CallInsightsBadge {
    sentiment?: SentimentLabel | null;
    reason?: CallReason | null;
}
export interface Call {
    callId: number;
    primaryContact: {
        contactId: number;
        firstName: string;
        lastName: string;
        iconColor: ColorKey;
        availability: string;
        ownerEmail?: string;
        contactEmail?: string;
        contactType?: { source: string };
        url?: string;
    };
    duplicatedContacts: {
        contactId: number;
        firstName: string;
        lastName: string;
        availability: string;
    }[];
    type: CallState;
    clientMsisdn: number;
    internalCallee?: CallUser | null;
    internalCaller?: CallUser | null;
    exposedMsisdn: {
        msisdn: number;
        label: string;
        type: string;
        icon: string;
        iconColor: string;
    };
    startedAt: string;
    bridgetAt: string;
    finishedAt: string;
    user?: {
        userId: number;
        firstName: string;
        lastName: string;
        avatarUrl: string;
        contactType?: { source: string };
        url?: string;
    },
    forwardedTo: number;
    forwardedAt: string;
    recordingId: number;
    spamCaller: boolean;
    wasDuringDnd?: boolean;
    hasVoicemail?: boolean;
    platform?: string;
    callType?: CallKind | null;
    roomId?: string | null;
    participants?: CallParticipant[] | null;
    myOutcome?: GroupCallOutcome | null;
    callIntent?: "audio" | "video" | null;
    recording?: CallRecordingInfo | null;
    insights?: CallInsightsBadge | null;
}

interface CallList {
    calls: Call[];
    totalCount: number;
}

export enum CallStateEnum {
    INBOUND = 'INBOUND',
    OUTBOUND = 'OUTBOUND',
}

export interface TranscriptSegment {
    userId?: number | null;
    clientLabel?: string | null;
    segmentId: number;
    startTime: number;
    endTime: number;
    text?: string | null;
    callee?: number | string | null;
    channelId?: number | string | null;
}

export interface CallTranscript {
    callId: number;
    language?: string | null;
    createdBy?: number | null;
    transcript: TranscriptSegment[];
}

export interface Citation {
    segmentId: number;
    startTime: number;
}

export interface InsightPoint {
    text: string;
    citations?: Citation[] | null;
}

export interface InsightNextStep extends InsightPoint {
    owner?: NextStepOwner | null;
}

export interface InsightTopic {
    label: string;
    citations?: Citation[] | null;
}

export interface DetectedMeeting {
    title: string;
    date: string;
    time?: string | null;
    startsAt?: string | null;
    timeZone?: string | null;
    durationMinutes?: number | null;
    location?: string | null;
    citations?: Citation[] | null;
}

export interface DetectedContactDetails {
    firstName?: string | null;
    lastName?: string | null;
    company?: string | null;
    email?: string | null;
    citations?: Citation[] | null;
}

export interface CallSentiment {
    label: SentimentLabel;
    rationale?: string | null;
}

export interface CallInsights {
    headline?: string | null;
    keyPoints?: InsightPoint[] | null;
    nextSteps?: InsightNextStep[] | null;
    reason?: CallReason | null;
    outcome?: CallOutcome | null;
    sentiment?: CallSentiment | null;
    topics?: InsightTopic[] | null;
    meetings?: DetectedMeeting[] | null;
    contactDetails?: DetectedContactDetails | null;
}

export interface CallSummary {
    callId: number;
    summary?: string | null;
    insightsVersion?: number | null;
    insights?: CallInsights | null;
}

export interface FollowUpDraft {
    channel: FollowUpChannel;
    subject?: string | null;
    body: string;
}

export interface CallQuestion {
    id: number;
    question: string;
    answer: string;
    answerable: boolean;
    citations?: Citation[] | null;
    createdAt: string;
}

export type CallAiError = FetchBaseQueryError & { retryAfterSeconds?: number | null };

const withRetryAfter = (error: FetchBaseQueryError, meta: FetchBaseQueryMeta | undefined): CallAiError => {
    const header = meta?.response?.headers.get('Retry-After');
    const seconds = header ? Number(header) : NaN;
    return {...error, retryAfterSeconds: Number.isFinite(seconds) ? seconds : null};
};

export interface CallHistoryParams {
    from?: string;
    to?: string;
    contacts?: number[];
    users?: number[];
    groups?: number | number[];
    phoneNumbers?: number | number[];
    limit?: number;
    userId?: string | number;
    phrase?: string;
}
export interface MatrixCallsHistory {
    roomId: string,
    callId: number,
    callState: string,
    startedAt: string;
    participantIds: number[];
    matrixCallId?: string;
    alreadyHangedUpUsers: number[]

}

export interface ContactCallHistory {
    groupedCalls: { [key: string]: Call[] };
    loadedCount: number;
}

export const callsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        fetchHistoryList: builder.query<CallList, CallHistoryParams>({
            query: ({ from, to, contacts, users, groups, phoneNumbers, limit, userId, phrase }) => ({
                url: `/api/v1/calls`,
                params: { userId, from, to, contacts, users, groups, phoneNumbers, limit, phrase },
            }),
            transformResponse: (response: { data: Call[] }, meta) => {
                const totalCount = meta?.response?.headers.get('x-total-count');
                return {
                    calls: unwrapData(response) || [],
                    totalCount: totalCount ? parseInt(totalCount, 10) : 0,
                };
            },
            providesTags: (_, __, arg) => [
                { type: 'Contact',id: arg.userId },
                {type: 'Numbers'},
                {type: 'Calls', id: 'HISTORY'},
            ],

        }),
        fetchContactHistory: builder.query<ContactCallHistory, { contactId: number, phrase?: string, userId?: string, limit?: number, users?: number[], phoneNumbers?: number[], from?: string, to?: string }>({
            query: ({contactId, phrase, limit, users, phoneNumbers, from, to}) => ({
                url:`/api/v1/contacts/${contactId}/calls`,
                params: { phrase, limit, users, phoneNumbers, from, to }
            }),
            transformResponse: (response: { data: Call[] }) => {
                const calls = unwrapData(response) || [];
                const groupedCalls = calls.reduce((acc: { [key: string]: Call[] }, call) => {
                    const date = call.startedAt?.split('T')[0] || 'unknown';
                    if (!acc[date]) {
                        acc[date] = [];
                    }
                    acc[date].push(call);
                    return acc;
                }, {});
                return {
                    groupedCalls,
                    loadedCount: calls.length,
                };
            },
        }),
        fetchCall: builder.query<Call, number>({
            query: (callId) => `/api/v1/calls/${callId}`,
            transformResponse: (response: { data: Call }) => unwrapData(response),
            providesTags: (_result, _error, callId) => [{ type: 'Call', id: callId }],
        }),
        fetchCallRecord: builder.query<string, number>({
            query: (recordingId) => ({
                url: `/api/v1/recordings/${recordingId}`,
                responseHandler: (response: Response) => response.blob(),
            }),
            transformResponse: (data: Blob) => {
                const audioBlob = new Blob([data], { type: 'audio/mpeg' });
                return URL.createObjectURL(audioBlob);
            },
        }),
        fetchOngoingCall: builder.query<Call[], CallHistoryParams>({
            query: ({ from, to, contacts, users, groups, phoneNumbers, limit, userId }) => ({
                url: `/api/v1/calls`,
                params: { userId, from, to, contacts, users, groups, phoneNumbers, limit, ongoing: true },
            }),
            transformResponse: (response: { data: Call[] }) => unwrapData(response),
            providesTags: [{type: 'Calls', id: 'ONGOING'}],
        }),

        fetchInboundQueueCall: builder.query<Call[], string | null>({
            query: (userId) => ({
                url: `/api/v1/calls`,
                params: { userId, callState: 'STARTED' },
            }),
            transformResponse: (response: { data: Call[] }) =>
                unwrapData(response).filter((call) => call.type === CallStateEnum.INBOUND),
        }),
        fetchCallTranscript: builder.query<CallTranscript, number>({
            query: (callId) => `/api/v1/calls/${callId}/transcripts`,
            providesTags: (_result, _error, callId) => [
                { type: 'Call', id: 'LIST' },
                { type: 'CallTranscript', id: callId }
            ],
        }),
        requestCallTranscription: builder.mutation<CallTranscript, {callId: number }>({
            query: ({ callId}) => ({
                url: `/api/v1/calls/${callId}/transcripts`,
                method: 'POST',
                body:{  },
            }),
            invalidatesTags: (_result, _error, { callId }) => [
                { type: 'CallTranscript', id: callId },
                { type: 'CallSummary', id: callId }
            ],
        }),
        requestCallSummary: builder.mutation<CallSummary, {callId: number }>({
            query: ({ callId}) => ({
                url: `/api/v1/calls/${callId}/summaries`,
                method: 'POST',
                body:{  },
            }),
            transformErrorResponse: withRetryAfter,
            invalidatesTags: (_result, _error, { callId }) => [
                { type: 'CallTranscript', id: callId },
                { type: 'CallSummary', id: callId },
                { type: 'Calls', id: 'HISTORY' },
            ],
        }),
        fetchCallSummary: builder.query<CallSummary, number>({
            query: (callId) => `/api/v1/calls/${callId}/summaries`,
            providesTags: (_result, _error, callId) => [
                { type: 'Call', id: 'LIST' },
                { type: 'CallSummary', id: callId }
            ],
        }),
        createFollowUpDraft: builder.mutation<FollowUpDraft, {callId: number; channel: FollowUpChannel}>({
            query: ({callId, channel}) => ({
                url: `/api/v1/calls/${callId}/follow-up-drafts`,
                method: 'POST',
                body: {channel},
            }),
            transformResponse: (response: { data: FollowUpDraft }) => unwrapData(response),
            transformErrorResponse: withRetryAfter,
        }),
        fetchCallQuestions: builder.query<CallQuestion[], number>({
            query: (callId) => `/api/v1/calls/${callId}/questions`,
            transformResponse: (response: { data: CallQuestion[] }) => unwrapData(response) ?? [],
            providesTags: (_result, _error, callId) => [{ type: 'CallQuestions', id: callId }],
        }),
        askCallQuestion: builder.mutation<CallQuestion, {callId: number; question: string}>({
            query: ({callId, question}) => ({
                url: `/api/v1/calls/${callId}/questions`,
                method: 'POST',
                body: {question},
            }),
            transformResponse: (response: { data: CallQuestion }) => unwrapData(response),
            transformErrorResponse: withRetryAfter,
            async onQueryStarted({callId}, {dispatch, queryFulfilled}) {
                try {
                    const {data: asked} = await queryFulfilled;
                    dispatch(callsApi.util.updateQueryData('fetchCallQuestions', callId, (questions) => {
                        if (!questions.some((item) => item.id === asked.id)) {
                            questions.push(asked);
                        }
                    }));
                } catch {
                    return;
                }
            },
        }),
        fetchCallsHistory: builder.query<{data: MatrixCallsHistory[] }, { userId: string }>({
            query: ({userId}) => ({
                url:`/api/v1/call-history/chat-state/?userId=${userId}&callState=STARTED,TALKING`,
            }),
        }),
    }),
});

export const invalidateCallInsights = (callId: number) => callsApi.util.invalidateTags([
    { type: 'Call', id: callId },
    { type: 'Calls', id: 'HISTORY' },
    { type: 'CallSummary', id: callId },
    { type: 'CallTranscript', id: callId },
]);

export const {
    useFetchHistoryListQuery,
    useFetchContactHistoryQuery,
    useFetchCallQuery,
    useFetchCallRecordQuery,
    useFetchOngoingCallQuery,
    useFetchInboundQueueCallQuery,
    useLazyFetchCallRecordQuery,
    useFetchCallTranscriptQuery,
    useRequestCallTranscriptionMutation,
    useRequestCallSummaryMutation,
    useFetchCallSummaryQuery,
    useCreateFollowUpDraftMutation,
    useFetchCallQuestionsQuery,
    useAskCallQuestionMutation,
    useFetchCallsHistoryQuery

} = callsApi;
